/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useCallback, useEffect, useState } from "react";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { RefreshCw, Coins } from "lucide-react";

/* USD→CNY 汇率面板：今日自动汇率（欧洲央行参考价）+ 按月手工覆盖 */
export function FxRatePanel({ zh, canManage, onChanged }) {
  const { api } = useApp();
  const [d, setD] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rate, setRate] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/finance/fx/recent", { params: { days: 30 } }).then(({ data }) => setD(data)).catch(() => setD(null)), [api]);
  useEffect(() => { load(); }, [load]);
  const save = async (clear = false) => {
    setBusy(true);
    try {
      await api.put("/finance/fx/manual", { month, rate: clear ? null : Number(rate) });
      toast.success(clear ? (zh ? `${month} 已恢复自动汇率` : "Reverted to automatic") : (zh ? `${month} 手工汇率已保存` : "Manual rate saved"));
      setRate(""); load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  if (!d) return null;
  const t = d.today || {};
  const manual = d.manual || {};
  const src = t.source === "manual" ? (zh ? "手工月汇率" : "manual") : t.source === "ecb" ? (zh ? "欧洲央行参考价" : "ECB reference") : (zh ? "最近缓存" : "cached");
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-5 py-4" data-testid="fx-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Coins className="h-4 w-4 text-teal-600" />
        <h3 className="font-semibold text-slate-900">{zh ? "美元 / 人民币汇率" : "USD / CNY rate"}</h3>
        <span className="text-sm text-slate-700" data-testid="fx-today">1 USD = <b data-testid="fx-today-rate">{t.rate}</b> CNY</span>
        <span className="text-[11px] text-slate-400" data-testid="fx-today-source">{src}{t.usedDate ? ` · ${t.usedDate}` : ""}</span>
        <button data-testid="fx-refresh" onClick={load} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-teal-600"><RefreshCw className="h-3.5 w-3.5" /></button>
        <span className="ml-auto text-[11px] text-slate-400">{zh ? "每日自动更新；成都 ¥ 记录按记账日汇率折算美元" : "Auto-updated daily; Chengdu CNY entries convert at the entry-date rate"}</span>
      </div>
      {Object.keys(manual).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="fx-manual-list">
          {Object.entries(manual).sort().map(([m, r]) => (
            <span key={m} className="text-[11px] px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid={`fx-manual-${m}`}>{m} · {zh ? "手工" : "manual"} {r}</span>
          ))}
        </div>
      )}
      {canManage && (
        <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="fx-manual-form">
          <span className="text-xs text-slate-500">{zh ? "按月手工覆盖：" : "Manual override by month:"}</span>
          <input type="month" data-testid="fx-manual-month" value={month} onChange={(e) => setMonth(e.target.value)} className="text-sm rounded-md border border-slate-200 px-2 py-1 bg-white" />
          <input type="number" step="0.0001" min="3" max="15" data-testid="fx-manual-rate" value={rate} onChange={(e) => setRate(e.target.value)} placeholder={zh ? "1 USD = ? CNY" : "1 USD = ? CNY"} className="text-sm rounded-md border border-slate-200 px-2 py-1 w-36 bg-white" />
          <Button size="sm" data-testid="fx-manual-save" disabled={busy || !Number(rate)} className="h-8 text-xs bg-teal-600 hover:bg-teal-700" onClick={() => save(false)}>{zh ? "保存" : "Save"}</Button>
          {manual[month] && <Button size="sm" variant="outline" data-testid="fx-manual-clear" disabled={busy} className="h-8 text-xs" onClick={() => save(true)}>{zh ? "恢复自动" : "Revert"}</Button>}
        </div>
      )}
    </div>
  );
}
